<template>
  <footer class="app-footer" :class="{ 'app-footer--compact': compact, 'app-footer--hide-on-mobile': hideOnMobile }">
    <div class="app-footer-inner">
      <div v-if="!compact" class="app-footer-columns">
        <div class="app-footer-col app-footer-col--about">
          <h3 class="app-footer-heading">About Us</h3>
          <p class="app-footer-about">
            We Search Research is on a mission to make clinical trials more accessible and easier to understand for patients and caregivers. 
          </p>
        </div>

        <div class="app-footer-col">
          <h3 class="app-footer-heading">Explore</h3>
          <ul class="app-footer-list">
            <li>
              <RouterLink :to="ROUTES.ABOUT_US" class="app-footer-link">
                About Us
              </RouterLink>
            </li>
            <li><a href="/how-it-works" class="app-footer-link">How it works</a></li>
          </ul>
        </div>

        <div class="app-footer-col">
          <h3 class="app-footer-heading">For partners</h3>
          <ul class="app-footer-list">
            <li>
              <RouterLink :to="ROUTES.BECOME_PARTNER" class="app-footer-link">
                Become a partner
              </RouterLink>
            </li>
            <li>
              <RouterLink :to="ROUTES.RESEARCHER_LOGIN" class="app-footer-link">
                Researcher Login
              </RouterLink>
            </li>
          </ul>
        </div>

        <div class="app-footer-col">
          <h3 class="app-footer-heading">Help</h3>
          <ul class="app-footer-list">
            <li><a href="/faq" class="app-footer-link">FAQ</a></li>
            <li><a href="#contact" class="app-footer-link">Contact</a></li>
          </ul>
        </div>
      </div>

      <div class="app-footer-bottom">
        <div class="app-footer-brand">
          <img src="@/assets/logo.png" alt="" class="app-footer-logo" />
          <span class="app-footer-brand-text">
            We <strong>Search</strong> Research
          </span>
        </div>

        <p class="app-footer-copy">&copy; We Search Research Inc. 2026</p>

        <nav class="app-footer-legal" aria-label="Legal">
          <RouterLink :to="ROUTES.PRIVACY_POLICY" class="app-footer-link">
            Privacy
          </RouterLink>
          <RouterLink :to="ROUTES.TERMS_OF_USE" class="app-footer-link">
            Terms
          </RouterLink>
          <!-- <a href="#cookies" class="app-footer-link">Cookies</a> -->
        </nav>
      </div>
    </div>
  </footer>
</template>

<script setup>
import { ROUTES } from '@/constants'

defineProps({
  compact: {
    type: Boolean,
    default: false
  },
  hideOnMobile: {
    type: Boolean,
    default: false
  }
})
</script>

<style scoped>
.app-footer {
  background: #10372f;
  color: #faf9f7;
  padding: 2.5rem 5rem;
  border-top: 1px solid rgba(154, 163, 126, 0.2);
}

.app-footer-inner {
  max-width: 1280px;
  margin: 0 auto;
}

.app-footer-columns {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 1.5rem;
  padding-bottom: 2.5rem;
  border-bottom: 1px solid rgba(154, 163, 126, 0.2);
}

.app-footer-col--about {
  max-width: 320px;
}

.app-footer-heading {
  color: #5e9b92;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0;
  margin: 0 0 1rem;
}

.app-footer-col--about h3.app-footer-heading {
  font-size: 1.5rem;
}

.app-footer-about {
  font-size: 1.125rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  max-width: 31rem;
}

.app-footer-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 0.6rem;
}

.app-footer-link {
  color: #ffffff;
  text-decoration: none;
  font-size: 0.9375rem;
  opacity: 0.9;
  transition: opacity 0.15s ease, color 0.15s ease;
}

.app-footer-link:hover {
  opacity: 1;
  color: #cfe1dc;
}

.app-footer-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding-top: 1.5rem;
  flex-wrap: wrap;
}

/* Compact variant — used on the chat page */
.app-footer--compact {
  padding: 1rem 5rem;
}

.app-footer--compact .app-footer-bottom {
  padding-top: 0;
}

.app-footer-brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.app-footer-logo {
  width: 28px;
  height: 28px;
  object-fit: contain;
}

.app-footer-brand-text {
  color: #ffffff;
  font-size: 0.9375rem;
  font-weight: 500;
}

.app-footer-brand-text strong {
  font-weight: 700;
}

.app-footer-copy {
  margin: 0;
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.7);
}

.app-footer-legal {
  display: flex;
  gap: 1.25rem;
  font-size: 0.8125rem;
}

@media (max-width: 900px) {
  .app-footer-columns {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  .app-footer-col--about {
    grid-column: 1 / -1;
    max-width: none;
  }
}

@media (max-width: 640px) {
  .app-footer {
    padding: 2rem 1rem 1.25rem;
  }

  .app-footer-columns {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    padding-bottom: 1.75rem;
  }

  .app-footer-bottom {
    justify-content: flex-start;
    gap: 1rem;
  }
}

@media (max-width: 768px) {
  .app-footer--hide-on-mobile {
    display: none;
  }
}
</style>

